import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment.prod';

export type BankEnum =
  | 'CBZ_BANK' | 'FBC_BANK' | 'CABS' | 'POSB' | 'ZB_BANK' | 'NMB_BANK'
  | 'STANBIC_BANK_ZIMBABWE' | 'STANDARD_CHARTERED_BANK_ZIMBABWE'
  | 'ECOBANK_ZIMBABWE' | 'NEDBANK_ZIMBABWE' | 'STEWARD_BANK' | 'BANC_ABC'
  | 'AFC_BANK' | 'METBANK' | 'FIRST_CAPITAL_BANK' | 'NATIONAL_BUILDING_SOCIETY'
  | 'ZIMBABWE_WOMENS_MICROFINANCE_BANK' | 'INFRASTRUCTURE_DEVELOPMENT_BANK_OF_ZIMBABWE'
  | 'EMPOWER_BANK' | 'SUCCESS_MICROFINANCE_BANK' | 'GETBUCKS_BANK' | 'INN_BUCKS_BANK'
  | 'SMEDCO_BANK' | 'TIME_BANK_OF_ZIMBABWE' | 'TETRAD_INVESTMENT_BANK'
  | 'LION_BANK' | 'AFRICAN_CENTURY_BANK' | 'ICECASH';

export type ColumnNameEnum =
  | 'TRANSACTION_DATE' | 'VALUE_DATE' | 'DESCRIPTION'
  | 'REFERENCE' | 'DEBIT' | 'CREDIT' | 'BALANCE';

export type TransactionSource =
  | 'POS' | 'MOBILE_MONEY' | 'BANK_DEPOSIT' | 'BANK_STATEMENT' | 'CASH_COLLECTION';

export interface Currency {
  id: number;
  name: string;
  code: string;
}

export interface BankReconciliationTransaction {
  id: number;
  transactionDate: string;
  valueDate: string;
  description: string;
  reference: string;
  debit: number;
  credit: number;
  balance: number;
  currency: Currency;
  bank: string;
}

export interface AllocatedTransaction {
  id: number;
  transactionDate: string;
  description: string;
  reference: string;
  debit: number;
  credit: number;
  user: any;
  posCode: string;
}

export interface BankMappingRequest {
  columnName: ColumnNameEnum;
  columnIndex: number;
}

export interface BankColumnMapping {
  id: number;
  columnName: string;
  bank: string;
  columnIndex: number;
  numberOfHeadersToSkip: number;
}

export interface PosBankConfiguration {
  id: number;
  bank: string;
  regexPattern: string;
  columnName: string;
}

export interface UserPosMapping {
  id: number;
  user: any;
  bank: string;
  posCode: string;
  deviceName: string;
}

export interface UserDailyCollection {
  id: number;
  transactionDate: string;
  description: string;
  reference: string;
  debit: number;
  credit: number;
  currency: Currency;
  transactionSource: string;
}

export interface GenerateBankReconciliationTransactionResponse {
  message: string;
  bankReconciliationTransactions: BankReconciliationTransaction[];
}

export interface GenerateBankMappingResponse {
  message: string;
  bankColumnMappings: BankColumnMapping[];
}

export interface GeneratePosConfigurationResponse {
  message: string;
  posBankConfiguration: PosBankConfiguration;
}

export interface GeneratePosAllocationResponse {
  message: string;
  userPosMappings: UserPosMapping[];
}

export interface GenerateAgentSalesResponse {
  message: string;
  userDailyCollection: UserDailyCollection;
}

export interface GenerateProcessTransactionResponse {
  message: string;
  allocatedBankReconciliationTransactions: AllocatedTransaction[];
  unAllocatedBankReconciliationTransactions: BankReconciliationTransaction[];
}

export interface ReconcileResult {
  user: string;
  salesSummary: { usd: number; zwl: number };
  bankSummary: { usd: number; zwl: number };
  variance: { usd: number; zwl: number };
}

export interface CreateDailyCollectionRequest {
  transactionDate: string;
  description: string;
  reference: string;
  paymentAmount: number;
  currencyId: number;
  transactionSource: TransactionSource;
}

@Injectable({ providedIn: 'root' })
export class BankReconciliationService {
  private readonly http = inject(HttpClient);
  private readonly base = environment.apiUrl;

  getCurrencies(): Observable<{ currencies: Currency[] }> {
    return this.http.get<{ currencies: Currency[] }>(`${this.base}/api/currency`);
  }

  importStatement(file: File, bank: BankEnum, currencyId: number): Observable<GenerateBankReconciliationTransactionResponse> {
    const params = new HttpParams().set('bank', bank).set('currencyId', currencyId);
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<GenerateBankReconciliationTransactionResponse>(
      `${this.base}/api/bank-statement-import`, formData, { params }
    );
  }

  saveBankMapping(payload: {
    bank: BankEnum;
    numberOfHeadersToSkip: number;
    bankMappingRequests: BankMappingRequest[];
  }): Observable<GenerateBankMappingResponse> {
    return this.http.post<GenerateBankMappingResponse>(`${this.base}/api/bank-mapping`, payload);
  }

  savePosConfig(payload: {
    bank: BankEnum;
    regexPattern: string;
    columnName: ColumnNameEnum;
  }): Observable<GeneratePosConfigurationResponse> {
    return this.http.post<GeneratePosConfigurationResponse>(`${this.base}/api/pos-configuration`, payload);
  }

  savePosAllocation(payload: {
    userId: number;
    bank: BankEnum;
    posCode: string;
  }): Observable<GeneratePosAllocationResponse> {
    return this.http.post<GeneratePosAllocationResponse>(`${this.base}/api/pos-allocation`, payload);
  }

  createAgentSale(payload: CreateDailyCollectionRequest): Observable<GenerateAgentSalesResponse[] | GenerateAgentSalesResponse> {
    return this.http.post<GenerateAgentSalesResponse[] | GenerateAgentSalesResponse>(`${this.base}/api/agent-sales`, payload);
  }

  getAgentSales(): Observable<{ userDailyCollections: UserDailyCollection[] }> {
    return this.http.get<{ userDailyCollections: UserDailyCollection[] }>(`${this.base}/api/agent-sales`);
  }

  getUnallocatedTransactions(): Observable<{ bankReconciliationTransactions: BankReconciliationTransaction[] }> {
    return this.http.get<{ bankReconciliationTransactions: BankReconciliationTransaction[] }>(
      `${this.base}/api/bank-reconciliation-transactions`
    );
  }

  findTransactions(bank: BankEnum, isAllocated: boolean): Observable<{ bankReconciliationTransactions: BankReconciliationTransaction[] }> {
    return this.http.post<{ bankReconciliationTransactions: BankReconciliationTransaction[] }>(
      `${this.base}/api/allocation/find`, { bank, isAllocated }
    );
  }

  runAutomatic(bank: BankEnum): Observable<GenerateProcessTransactionResponse> {
    const params = new HttpParams().set('bank', bank);
    return this.http.post<GenerateProcessTransactionResponse>(
      `${this.base}/api/allocation/automatic`, {}, { params }
    );
  }

  runManual(userId: number, bankReconciliationTransactionId: number[]): Observable<GenerateProcessTransactionResponse> {
    return this.http.post<GenerateProcessTransactionResponse>(
      `${this.base}/api/allocation/manual`, { bankReconciliationTransactionId, userId }
    );
  }

  uploadZinaraReport(formData: FormData): Observable<any> {
    return this.http.post<any>(`${this.base}/file-upload/zinara-report`, formData);
  }

  runReconcile(dateFrom: string, dateTo: string): Observable<ReconcileResult[]> {
    return this.http.post<ReconcileResult[]>(`${this.base}/api/allocation/reconcile`, { dateFrom, dateTo });
  }
}
